import { useState } from 'react';
import { Navigate, Link, useLocation } from 'react-router-dom';
import { useDashboard } from '@/context/DashboardContext';
import { Lock, Loader2, Eye, EyeOff } from 'lucide-react';
import { motion } from 'framer-motion';
import { useSiteLang } from '@/components/site/SiteLang';
import DashLangSwitch from '@/components/dashboard/DashLangSwitch';

const T = {
  es: {
    homeAria: 'Volver a la web de Freedom Homes Club',
    title: 'Área de socios',
    subtitle: 'Accede a tu formación, tu comunidad y tus documentos',
    email: 'Email',
    emailPh: 'tu@email.com',
    password: 'Contraseña',
    forgot: '¿Has olvidado tu contraseña?',
    passwordPh: 'Tu contraseña',
    hide: 'Ocultar contraseña',
    show: 'Mostrar contraseña',
    loading: 'Accediendo...',
    submit: 'Acceder',
    noAccount: '¿No tienes cuenta?',
    create: 'Crear cuenta',
    back: '← Volver a la web',
  },
  en: {
    homeAria: 'Back to the Freedom Homes Club website',
    title: 'Members’ area',
    subtitle: 'Access your training, your community and your documents',
    email: 'Email',
    emailPh: 'you@email.com',
    password: 'Password',
    forgot: 'Forgotten your password?',
    passwordPh: 'Your password',
    hide: 'Hide password',
    show: 'Show password',
    loading: 'Signing in…',
    submit: 'Sign in',
    noAccount: 'Don’t have an account?',
    create: 'Create account',
    back: '← Back to the website',
  },
};

export default function DashboardLogin() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login, user } = useDashboard();
  const location = useLocation();
  const { lang } = useSiteLang();
  const t = T[lang] || T.es;
  // A dónde volver tras entrar: la página protegida que pidió el login o en la que caducó la sesión
  let saved = null;
  try { saved = sessionStorage.getItem('dashboard_return_to'); } catch { /* sin almacenamiento */ }
  const candidate = location.state?.from || saved;
  const from = typeof candidate === 'string' && candidate.startsWith('/dashboard') && !candidate.startsWith('/dashboard/login') ? candidate : '/dashboard';

  if (user) {
    try { sessionStorage.removeItem('dashboard_return_to'); } catch { /* nada */ }
    return <Navigate to={from} replace />;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    const result = await login(email, password);
    setLoading(false);
    // Si entra bien, el propio componente redirige (user ya existe)
    if (!result.success) setError(result.error);
  };

  return (
    <div className="min-h-screen bg-[#060d1b] flex items-center justify-center px-4 relative overflow-hidden">
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 -left-32 w-96 h-96 bg-[#c9a84c]/[0.03] rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 -right-32 w-96 h-96 bg-[#c9a84c]/[0.02] rounded-full blur-3xl" />
      </div>

      <div className="fixed top-3 right-3 z-20"><DashLangSwitch /></div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-md relative z-10"
      >
        <div className="text-center mb-8">
          <Link to="/" className="inline-block mb-6 rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-[#c9a84c]/60" aria-label={t.homeAria} data-testid="login-logo-home">
            <img src="/logo-fhc-sm.png" alt="Freedom Homes Club" className="h-16 w-auto mx-auto" />
          </Link>
          <h1 className="text-2xl font-bold text-white mb-2 tracking-tight">{t.title}</h1>
          <p className="text-gray-400 text-sm">{t.subtitle}</p>
        </div>

        <form onSubmit={handleSubmit} className="bg-white/[0.02] backdrop-blur-xl border border-white/[0.06] rounded-2xl p-8 space-y-5" data-testid="dashboard-login-form">
          <div>
            <label htmlFor="login-email" className="block text-[11px] text-gray-400 uppercase tracking-wider font-medium mb-2">{t.email}</label>
            <input
              id="login-email" type="email" value={email} onChange={e => setEmail(e.target.value)} required disabled={loading}
              className="w-full bg-white/[0.03] border border-white/[0.06] rounded-xl px-4 py-3 text-white text-sm focus:outline-none focus:border-[#c9a84c]/30 transition-colors disabled:opacity-50 placeholder:text-gray-500"
              placeholder={t.emailPh} data-testid="login-email"
            />
          </div>
          <div>
            <div className="flex items-baseline justify-between mb-2">
              <label htmlFor="login-password" className="block text-[11px] text-gray-400 uppercase tracking-wider font-medium">{t.password}</label>
              <Link to="/dashboard/forgot-password" className="text-xs text-[#c9a84c] hover:underline" data-testid="login-forgot-link">{t.forgot}</Link>
            </div>
            <div className="relative">
              <input
                id="login-password" type={showPassword ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)} required disabled={loading}
                className="w-full bg-white/[0.03] border border-white/[0.06] rounded-xl px-4 py-3 pr-11 text-white text-sm focus:outline-none focus:border-[#c9a84c]/30 transition-colors disabled:opacity-50 placeholder:text-gray-500"
                placeholder={t.passwordPh} data-testid="login-password"
              />
              <button type="button" onClick={() => setShowPassword(v => !v)} aria-label={showPassword ? t.hide : t.show} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-400 transition-colors" data-testid="login-password-toggle">
                {showPassword ? <EyeOff size={15} /> : <Eye size={15} />}
              </button>
            </div>
          </div>
          {error && <p className="text-red-400 text-sm" data-testid="login-error">{error}</p>}
          <button type="submit" disabled={loading} className="w-full bg-[#c9a84c] hover:bg-[#b8973f] text-[#0a1628] font-semibold py-3 rounded-xl transition-all flex items-center justify-center gap-2 disabled:opacity-50 shadow-lg shadow-[#c9a84c]/15" data-testid="login-submit">
            {loading ? <Loader2 size={16} className="animate-spin" /> : <Lock size={15} />}
            {loading ? t.loading : t.submit}
          </button>
          <p className="text-center text-sm text-gray-400 mt-3">
            {t.noAccount}{' '}
            <a href="/dashboard/register" className="text-[#c9a84c] hover:underline" data-testid="login-register-link">{t.create}</a>
          </p>
        </form>
        <p className="text-center mt-6">
          <Link to="/" className="text-xs text-gray-400 hover:text-white transition-colors" data-testid="login-back-home">{t.back}</Link>
        </p>
      </motion.div>
    </div>
  );
}
